import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar, ArrowRight, BookOpen } from 'lucide-react';
import { SEO } from '@/components/SEO';

const CATEGORIES = ['All', 'Dog Care', 'Training', 'Nutrition', 'First-time Owners'];

export default function BlogPage() {
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState('All');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const params = category !== 'All' ? { category } : {};
    api.getBlogPosts(params)
      .then(r => setPosts(r.data.posts || []))
      .catch(() => setPosts([]))
      .finally(() => setLoading(false));
  }, [category]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 animate-fade-in" data-testid="blog-page">
      <SEO
        title="Dog Care Blog"
        description="Dog care tips, puppy training guides, nutrition advice, and more. Everything you need to know about raising a happy, healthy dog."
        keywords="dog care tips, puppy training guide, how to take care of a dog, dog nutrition advice"
      />
      <div className="mb-8">
        <h1 className="font-['Nunito'] font-extrabold text-3xl sm:text-4xl text-[#1A1D20] tracking-tight">
          Dog Care Blog
        </h1>
        <p className="text-sm text-[#5C636A] mt-1">
          Tips, guides, and advice for dog owners
        </p>
      </div>

      {/* Category Tabs */}
      <div className="flex gap-2 mb-8 overflow-x-auto pb-2" data-testid="blog-categories">
        {CATEGORIES.map(cat => (
          <Button
            key={cat}
            variant={category === cat ? 'default' : 'outline'}
            size="sm"
            onClick={() => setCategory(cat)}
            className={`rounded-full shrink-0 ${
              category === cat ? 'bg-[#4A90E2] text-white' : 'hover:bg-blue-50 hover:text-[#4A90E2] hover:border-[#4A90E2]'
            }`}
            data-testid={`blog-category-${cat.toLowerCase().replace(/\s/g, '-')}`}
          >
            {cat}
          </Button>
        ))}
      </div>

      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="bg-white rounded-3xl overflow-hidden animate-pulse">
              <div className="aspect-video bg-gray-200" />
              <div className="p-6 space-y-3">
                <div className="h-4 bg-gray-200 rounded w-3/4" />
                <div className="h-3 bg-gray-200 rounded w-full" />
              </div>
            </div>
          ))}
        </div>
      ) : posts.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-posts-grid">
          {posts.map(post => (
            <Link
              key={post.slug}
              to={`/blog/${post.slug}`}
              className="group bg-white rounded-3xl overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_20px_40px_rgb(0,0,0,0.08)] transition-transform transition-shadow duration-300 hover:-translate-y-1"
              data-testid={`blog-card-${post.slug}`}
            >
              <div className="aspect-video overflow-hidden">
                <img
                  src={post.image_url || 'https://images.unsplash.com/photo-1587300003388-59208cc962cb?w=600'}
                  alt={post.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  loading="lazy"
                />
              </div>
              <div className="p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Badge className="bg-orange-50 text-[#F5A623] border-0 rounded-full text-xs font-medium">
                    {post.category}
                  </Badge>
                  <span className="text-xs text-[#5C636A] flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    {new Date(post.created_at).toLocaleDateString()}
                  </span>
                </div>
                <h3 className="font-['Nunito'] font-bold text-lg text-[#1A1D20] mb-2 line-clamp-2 group-hover:text-[#4A90E2] transition-colors">
                  {post.title}
                </h3>
                <p className="text-sm text-[#5C636A] line-clamp-2 leading-relaxed">{post.excerpt}</p>
                <div className="flex items-center gap-1 mt-4 text-sm font-medium text-[#4A90E2]">
                  Read More <ArrowRight className="w-3.5 h-3.5" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      ) : (
        <div className="text-center py-20">
          <BookOpen className="w-12 h-12 text-[#5C636A] mx-auto mb-3 opacity-40" />
          <p className="text-[#5C636A] text-lg">No blog posts found</p>
        </div>
      )}
    </div>
  );
}
